import { MeetingProvider, Constants, useMediaDevice } from "@videosdk.live/react-sdk";
import { useEffect, useState } from "react";
import { MeetingAppProvider } from "./MeetingAppContextDef";
import { MeetingContainer } from "./meeting/MeetingContainer";
import { LeaveScreen } from "./components/screens/LeaveScreen";
import { JoiningScreen } from "./components/screens/JoiningScreen";
import { useSearchParams } from "react-router-dom";
import { TokenJoinScreen } from "./components/screens/TokenJoinScreen";

function App() {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [micOn, setMicOn] = useState(false);
  const [webcamOn, setWebcamOn] = useState(false);
  const [customAudioStream, setCustomAudioStream] = useState(null);
  const [customVideoStream, setCustomVideoStream] = useState(null);
  const [isMeetingStarted, setMeetingStarted] = useState(false);
  const [isMeetingLeft, setIsMeetingLeft] = useState(false);
  
  // Token-based integration states
  const [isTokenFlow, setIsTokenFlow] = useState(false);
  const [tokenFlowData, setTokenFlowData] = useState(null);
  const [showTokenJoin, setShowTokenJoin] = useState(false);
  
  // Device permission states for token flow
  const [isTokenFlowReady, setIsTokenFlowReady] = useState(false);
  const [permissionsGranted, setPermissionsGranted] = useState(false);

  const { checkPermissions, requestPermission, getCameras, getMicrophones } = useMediaDevice();

  const isMobile = window.matchMedia("only screen and (max-width: 768px)").matches;

  // Check for token in URL parameters
  useEffect(() => {
    const joinToken = searchParams.get('token');
    const roomId = searchParams.get('roomId');
    const userName = searchParams.get('name') || 'Guest User';
    
    if (joinToken && roomId) {
      setIsTokenFlow(true);
      setTokenFlowData({
        token: joinToken,
        roomId: roomId,
        participantName: userName
      });
      
      // Set the states but don't start meeting yet
      setToken(joinToken);
      setMeetingId(roomId);
      setParticipantName(userName);
      
      // Check permissions first
      handleTokenFlowPermissions();
    }
  }, [searchParams]);

  // Handle device permissions for token flow
  const handleTokenFlowPermissions = async () => {
    try {
      console.log("Starting token flow permission check...");
      
      // Check current permissions
      const permissions = await checkPermissions();
      const cameraAllowed = permissions.get(Constants.permission.VIDEO);
      const micAllowed = permissions.get(Constants.permission.AUDIO);

      console.log("Current permissions:", { cameraAllowed, micAllowed });

      if (cameraAllowed && micAllowed) {
        // Permissions already granted, get devices
        await initializeDevicesAndStart();
      } else {
        // Request permissions
        console.log("Requesting permissions...");
        try {
          // Request audio permission
          if (!micAllowed) {
            await requestPermission(Constants.permission.AUDIO);
          }
          
          // Request video permission
          if (!cameraAllowed) {
            await requestPermission(Constants.permission.VIDEO);
          }
          
          // Wait a bit for permissions to be processed
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Recheck permissions after request
          const newPermissions = await checkPermissions();
          const newCameraAllowed = newPermissions.get(Constants.permission.VIDEO);
          const newMicAllowed = newPermissions.get(Constants.permission.AUDIO);
          
          console.log("New permissions after request:", { newCameraAllowed, newMicAllowed });
          
          await initializeDevicesAndStart(newMicAllowed, newCameraAllowed);
        } catch (error) {
          console.warn("Permission request failed, joining without media:", error);
          // Join without media if permissions denied
          setMicOn(false);
          setWebcamOn(false);
          setPermissionsGranted(true);
          setIsTokenFlowReady(true);
          setMeetingStarted(true);
        }
      }
    } catch (error) {
      console.error("Error checking permissions:", error);
      // Fallback: join without media
      setMicOn(false);
      setWebcamOn(false);
      setPermissionsGranted(true);
      setIsTokenFlowReady(true);
      setMeetingStarted(true);
    }
  };

  // Initialize devices and start meeting
  const initializeDevicesAndStart = async (micAllowed = true, cameraAllowed = true) => {
    try {
      console.log("Initializing devices...");
      
      // Get available devices
      const [cameras, microphones] = await Promise.all([
        cameraAllowed ? getCameras().catch(() => []) : [],
        micAllowed ? getMicrophones().catch(() => []) : []
      ]);

      console.log("Available devices:", { cameras: cameras.length, microphones: microphones.length });

      // Set media states based on device availability and permissions
      const canUseMic = micAllowed && microphones.length > 0;
      const canUseCamera = cameraAllowed && cameras.length > 0;

      setMicOn(canUseMic);
      setWebcamOn(canUseCamera);
      setPermissionsGranted(true);
      setIsTokenFlowReady(true);
      setMeetingStarted(true);

      console.log("Token flow ready:", { canUseMic, canUseCamera });
    } catch (error) {
      console.error("Error initializing devices:", error);
      // Still join the meeting, just without media
      setMicOn(false);
      setWebcamOn(false);
      setPermissionsGranted(true);
      setIsTokenFlowReady(true);
      setMeetingStarted(true);
    }
  };

  useEffect(() => {
    if (isMobile) {
      window.onbeforeunload = () => {
        return "Are you sure you want to exit?";
      };
    }
  }, [isMobile]);

  return (
    <>
      <MeetingAppProvider>
        {isMeetingStarted && (isTokenFlowReady || !isTokenFlow) ? (
          <MeetingProvider
            config={{
              meetingId,
              micEnabled: micOn,
              webcamEnabled: webcamOn,
              name: participantName ? participantName : "TestUser",
              multiStream: true,
              // Don't pass custom streams if they're null - let VideoSDK handle device selection
              ...(customVideoStream && { customCameraVideoTrack: customVideoStream }),
              ...(customAudioStream && { customMicrophoneAudioTrack: customAudioStream }),
            }}
            token={token}
            reinitialiseMeetingOnConfigChange={true}
            joinWithoutUserInteraction={isTokenFlow && isTokenFlowReady}
          >
            <MeetingContainer
              onMeetingLeave={() => {
                setToken("");
                setMeetingId("");
                setParticipantName("");
                setWebcamOn(false);
                setMicOn(false);
                setMeetingStarted(false);
                setIsTokenFlow(false);
                setTokenFlowData(null);
                setIsTokenFlowReady(false);
                setPermissionsGranted(false);
                setCustomAudioStream(null);
                setCustomVideoStream(null);
              }}
              setIsMeetingLeft={setIsMeetingLeft}
              isTokenFlow={isTokenFlow}
            />
          </MeetingProvider>
        ) : isMeetingLeft ? (
          <LeaveScreen setIsMeetingLeft={setIsMeetingLeft} />
        ) : showTokenJoin ? (
          <TokenJoinScreen
            onJoinWithToken={(tokenData) => {
              setToken(tokenData.token);
              setMeetingId(tokenData.roomId);
              setParticipantName(tokenData.participantName);
              setIsTokenFlow(true);
              handleTokenFlowPermissions();
              setShowTokenJoin(false);
            }}
            onBackToNormalFlow={() => setShowTokenJoin(false)}
          />
        ) : isTokenFlow && !isTokenFlowReady ? (
          // Show loading screen while setting up token flow
          <div className="fixed inset-0 bg-gray-800 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-lg">Setting up your meeting...</p>
              <p className="text-sm text-gray-300 mt-2">Checking device permissions</p>
              {permissionsGranted && (
                <p className="text-xs text-green-400 mt-1">✓ Permissions granted</p>
              )}
            </div>
          </div>
        ) : !isTokenFlow && (
          <JoiningScreen
            participantName={participantName}
            setParticipantName={setParticipantName}
            setMeetingId={setMeetingId}
            setToken={setToken}
            micOn={micOn}
            setMicOn={setMicOn}
            webcamOn={webcamOn}
            setWebcamOn={setWebcamOn}
            customAudioStream={customAudioStream}
            setCustomAudioStream={setCustomAudioStream}
            customVideoStream={customVideoStream}
            setCustomVideoStream={setCustomVideoStream}
            onClickStartMeeting={() => {
              setMeetingStarted(true);
            }}
            startMeeting={isMeetingStarted}
            setIsMeetingLeft={setIsMeetingLeft}
            onSwitchToTokenFlow={() => setShowTokenJoin(true)}
          />
        )}
      </MeetingAppProvider>
    </>
  );
}

export default App;
